/**
 * Category-appropriate stock fallback images.
 *
 * Used in two situations:
 *   1. A listing is marked `is_demo=true` on the backend — we swap its
 *      (seed) unrelated interior photos for the category-appropriate
 *      illustrative product shot.
 *   2. A listing has no `photo_url` / `photo_urls` at all (missing or
 *      broken) — we fall back to a category image so the UI never renders
 *      an empty grey rectangle.
 *
 * IMPORTANT: retailer-uploaded photos on real (non-demo) listings must
 * never be replaced. `resolveListingImages()` enforces this.
 */
export const CATEGORY_FALLBACKS = {
  carpet: {
    src: "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/fltuw8f4_Grey%20Carpet.png",
    alt: "Grey wool-blend carpet remnant partly unrolled on a warehouse floor",
  },
  vinyl: {
    src: "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/zo490vcg_Vynal.png",
    alt: "Wood-effect sheet vinyl flooring partly unrolled",
  },
  laminate: {
    src: "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/2f5byklq_Oak%20Flooring.png",
    alt: "Boxed oak-effect laminate planks with three planks laid out in front",
  },
  lvt: {
    src: "https://static.prod-images.emergentagent.com/jobs/aad5d1c2-1a67-46ce-9249-032835800159/images/9b47ad4fed6548905760a106e646704f4ec01e95338e27a3ea3a31447f3c18db.jpeg",
    alt: "Flat rigid stone-effect LVT planks stacked in an open box with one plank laid flat in front",
  },
  artificial_grass: {
    src: "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/xqb4hll4_Grass.png",
    alt: "Roll of artificial grass turf partly unfurled showing the pile",
  },
  carpet_tiles: {
    src: "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/yk2vo9cq_Tiles.png",
    alt: "Fanned stack of grey commercial carpet tiles next to their cardboard box",
  },
};

const IMG = "https://static.prod-images.emergentagent.com/jobs/aad5d1c2-1a67-46ce-9249-032835800159/images/";

// Colour/pattern-specific illustrative assets for demo or photo-less listings.
// First matching rule (tested against colour + title) wins; otherwise the
// category default above is used.
export const VARIANT_FALLBACKS = {
  carpet: [
    { re: /navy|blue|denim/i, src: `${IMG}73db2f70629c22c2edb53c4f477015d640e849e5186d9783f9237687fcbccf13.jpeg`, alt: "Navy blue carpet roll partly unrolled on a warehouse floor" },
    { re: /rust|chestnut|brown|terracotta|copper|autumn/i, src: `${IMG}00c1af8e38abb2855789c5424857ae9b4cbfaa4085ae8c9d1ee727862e5230cd.jpeg`, alt: "Rust-brown carpet roll partly unrolled on a warehouse floor" },
    { re: /beige|oatmeal|cream|taupe|sand|biscuit|ivory|mushroom|natural/i, src: `${IMG}30eb4263df6037d0d0c3852d918433bfc1403c1a51f75a21db6f22855142c006.jpeg`, alt: "Beige carpet roll partly unrolled on a warehouse floor" },
  ],
  vinyl: [
    { re: /herringbone|walnut/i, src: `${IMG}02f5eade0181b3bfca53db0f112f5dba0cd94d9be0c1ef9b0555b1a5c582414a.jpeg`, alt: "Walnut herringbone-effect sheet vinyl partly unrolled" },
    { re: /marble|white/i, src: `${IMG}f64fdecc73d5e1059bf5e96416484c133e6b8d6e4c32ea87bd22041618eda63c.jpeg`, alt: "White marble-effect sheet vinyl partly unrolled" },
    { re: /stone|slate|tile/i, src: `${IMG}34067160385f639844e0be53800eddad8bb5688c530ff8b2216c4f9081dfedce.jpeg`, alt: "Grey stone tile-effect sheet vinyl partly unrolled" },
    { re: /pine|weathered|white.?wash|grey.?wash/i, src: `${IMG}9fad3bff46cfa341b43c635e831f7b107d0a54b6ea13b13959edb2dd121e5aaa.jpeg`, alt: "Weathered pine-effect sheet vinyl partly unrolled" },
  ],
  laminate: [
    { re: /ash|silver|grey|gray/i, src: `${IMG}e12842392a771b408d5614aaf9a3d1ad28a029dc8d3114373bcb58d161f5a7cf.jpeg`, alt: "Silver ash-effect laminate planks in front of boxed packs" },
  ],
  lvt: [
    { re: /walnut|wood|oak|brown|plank/i, src: `${IMG}ec678d0d73ffa7ffe1d6da32580455658dfcb1976b3fe2889b8715064720e6af.jpeg`, alt: "Rigid walnut wood-effect LVT planks in an open box" },
  ],
};

function fallbackFor(l) {
  const cat = l?.flooring_type;
  const text = `${l?.colour || ""} ${l?.title || ""}`;
  const v = (VARIANT_FALLBACKS[cat] || []).find((r) => r.re.test(text));
  return v || CATEGORY_FALLBACKS[cat] || null;
}

/**
 * Return the images to render for a listing, respecting the priority order:
 *   1. Demo listings → category fallback (single illustrative image).
 *   2. Real listings with retailer photos → those photos, unchanged.
 *   3. Real listings with no photo → category fallback (illustrative).
 *   4. Neither → empty array (component renders a grey placeholder).
 *
 * `isFallback` is true whenever we've substituted a category image so the
 * calling component can show an "Illustrative image" caption.
 * `isUnclearDemo` flags listings where `is_demo` isn't a boolean — we do
 * NOT guess and simply show whatever the retailer stored (rule from spec).
 */
export function resolveListingImages(l) {
  const fallback = fallbackFor(l);
  const stored = (Array.isArray(l?.photo_urls) && l.photo_urls.length > 0)
    ? l.photo_urls
    : (l?.photo_url ? [l.photo_url] : []);
  const demoValue = l?.is_demo;
  const isUnclearDemo = typeof demoValue !== "boolean";

  if (demoValue === true && fallback) {
    return {
      photos: [fallback.src],
      isFallback: true,
      alt: fallback.alt,
      isUnclearDemo: false,
    };
  }
  if (stored.length > 0) {
    return {
      photos: stored,
      isFallback: false,
      alt: l?.title || "Listing photo",
      isUnclearDemo,
    };
  }
  if (fallback) {
    return {
      photos: [fallback.src],
      isFallback: true,
      alt: fallback.alt,
      isUnclearDemo,
    };
  }
  return { photos: [], isFallback: false, alt: l?.title || "Listing photo", isUnclearDemo };
}
